import type { ReactNode } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { DxRichSection } from '../DxRichSection';
import { SECTION_META } from './editorState';
import type { DxReportPayload, DxReportSectionId, TiptapDoc } from '../types';
import type { RichEditorPreset } from '@odo/shared-ui';

export interface SubStepDef<TId extends string = string> {
    id: TId;
    label: string;
    sectionId: DxReportSectionId;

    presets?: RichEditorPreset[];
}

interface SubStepSplitViewProps<TId extends string> {
    patientId: string;
    payload: DxReportPayload;
    subs: readonly SubStepDef<TId>[];
    activeSubId: TId;
    onJumpTo: (id: TId) => void;
    onDocChange: (id: DxReportSectionId, doc: TiptapDoc) => void;
    /** Returns true when the underlying section has usable content. */
    isComplete: (sectionId: DxReportSectionId) => boolean;
    readOnly?: boolean;
    renderActiveHeader?: (activeSubId: TId) => ReactNode;
}

/**
 * Generic full-bleed split layout used by any main step that wants vertical
 * sub-step navigation:
 *
 *   - Left rail (180pt): vertical list of sub-step buttons. Active = solid
 *     dark; completed sections show an emerald check.
 *   - Right column: rich-text editor toolbar pinned to the top of the
 *     column, editor body grows to fill remaining vertical space (`flush`
 *     chrome on `DxRichSection`).
 *
 * Skips the standard centered form container so the editor reclaims the
 * maximum possible writing area. Drops the per-section card chrome - the
 * sub-step name in the rail is the only label the user needs.
 *
 * Used by Clinical (Findings / Comments / Risks) and Recommendation
 * (Special consent / Orthopulse). Same component, different sub-step config.
 */
export function SubStepSplitView<TId extends string>({
    patientId,
    payload,
    subs,
    activeSubId,
    onJumpTo,
    onDocChange,
    isComplete,
    readOnly,
    renderActiveHeader
}: SubStepSplitViewProps<TId>) {
    const activeSub = subs.find((s) => s.id === activeSubId) ?? subs[0];
    const section = payload.sections.find((s) => s.id === activeSub.sectionId);
    if (!section) return null;
    const meta = SECTION_META[section.id];

    return (
        <div className="flex-1 min-h-0 flex">
            {/* Left rail - vertical sub-step nav */}
            <aside className="w-[180px] flex-shrink-0 border-r border-gray-200 bg-gray-50/50 overflow-y-auto">
                <nav className="flex flex-col p-2 gap-0.5">
                    {subs.map((sub) => {
                        const isActive = sub.id === activeSubId;
                        const completed = isComplete(sub.sectionId);
                        const baseClass =
                            'flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left ';
                        const stateClass = isActive
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-700 hover:bg-gray-100';
                        return (
                            <button
                                key={sub.id}
                                type="button"
                                onClick={() => onJumpTo(sub.id)}
                                aria-current={isActive ? 'page' : undefined}
                                className={baseClass + stateClass}
                            >
                                <span className="truncate">{sub.label}</span>
                                {completed && (
                                    <CheckCircleIcon
                                        className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? 'text-emerald-300' : 'text-emerald-500'}`}
                                    />
                                )}
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {/* Right column - optional header + flush editor (toolbar at top, body expands) */}
            <div className="flex-1 min-h-0 flex flex-col">
                {renderActiveHeader?.(activeSubId)}
                <DxRichSection
                    chrome="flush"
                    patientId={patientId}
                    value={section.tiptapJson}
                    onChange={(doc) => onDocChange(section.id, doc)}
                    placeholder={meta.placeholder}
                    readOnly={readOnly}
                    presets={activeSub.presets}
                />
            </div>
        </div>
    );
}
